import React, { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import FloatingADUWizard from '@/components/FloatingADUWizard';
import FloatingReserveButton from '@/components/FloatingReserveButton';

const VictorianADUDesignSanCarlos = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Victorian ADU Design San Carlos - Historic Elegance & Charm",
    "description": "Professional Victorian ADU design and construction services in San Carlos. Bringing ornate details and timeless elegance to your property. Free consultation available.",
    "url": "https://udaadu.com/victorian-adu-design-san-carlos",
    "mainEntity": {
      "@type": "Service",
      "name": "Victorian ADU Design San Carlos",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      }
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[600px] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/adu-consultation"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
        <Navigation />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Victorian ADU Design San Carlos
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto">
                Professional Victorian ADU design and construction services in San Carlos. Bringing ornate architectural details and timeless elegance to your property.
              </p>
              <Button 
                onClick={openCalendlyModal}
                size="lg" 
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg"
              >
                Get Free Consultation
              </Button>
            </div>
          </section>

          {/* Why Choose Section */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
                Why Choose Victorian ADU Design in San Carlos?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-purple-600">👑</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Historic Grandeur</h3>
                  <p className="text-gray-600">Captures the ornate beauty and sophisticated craftsmanship of the Victorian era with authentic details.</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-purple-600">🎨</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Ornate Details</h3>
                  <p className="text-gray-600">Features intricate millwork, decorative trim, and period-appropriate fixtures that showcase artisan craftsmanship.</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-purple-600">💎</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Timeless Value</h3>
                  <p className="text-gray-600">A distinctive style that never goes out of fashion, ensuring lasting appeal and property value enhancement.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
                Victorian ADU Design Features
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Gingerbread Trim</h3>
                  <p className="text-gray-600">Intricate decorative woodwork that adorns eaves, porches, and windows with classic Victorian charm.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Bay Windows</h3>
                  <p className="text-gray-600">Elegant protruding windows that maximize light and create distinctive architectural interest.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Turret Elements</h3>
                  <p className="text-gray-600">Rounded tower features that add dramatic visual appeal and unique interior spaces.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Rich Color Palettes</h3>
                  <p className="text-gray-600">Deep, sophisticated color schemes that highlight architectural details and create visual drama.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Ornate Moldings</h3>
                  <p className="text-gray-600">Elaborate interior trim work and crown moldings that showcase period-appropriate craftsmanship.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Period Fixtures</h3>
                  <p className="text-gray-600">Authentic Victorian-inspired lighting, hardware, and plumbing fixtures that complete the historic look.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Local Considerations */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
                San Carlos Local Considerations
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Historic Neighborhood Character</h3>
                  <p className="text-gray-600 mb-4">Our Victorian ADUs in San Carlos complement the area's historic charm while meeting modern standards:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Respects San Carlos' historic residential character</li>
                    <li>Complements existing Victorian and Craftsman homes</li>
                    <li>Uses period-appropriate materials and details</li>
                    <li>Enhances neighborhood architectural continuity</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Modern Integration</h3>
                  <p className="text-gray-600 mb-4">Designed to meet contemporary needs while preserving Victorian authenticity:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Meets San Carlos zoning and building requirements</li>
                    <li>Incorporates modern energy efficiency standards</li>
                    <li>Optimized for the Bay Area climate</li>
                    <li>Enhances property values and curb appeal</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Cost Section */}
          <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-800">
                Victorian ADU Investment in San Carlos
              </h2>
              <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
                Victorian ADUs typically range from $220,000 to $400,000 in San Carlos, with custom millwork and ornate details. 
                The distinctive style provides excellent long-term value and unique character.
              </p>
              <Button 
                onClick={openCalendlyModal}
                size="lg" 
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg"
              >
                Get Detailed Cost Estimate
              </Button>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Build Your Victorian ADU in San Carlos?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Let's create an elegant, ornate space that captures the grandeur and charm of Victorian architecture for your San Carlos property.
              </p>
              <Button 
                onClick={openCalendlyModal}
                size="lg" 
                variant="outline" 
                className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Schedule Free Consultation
              </Button>
            </div>
          </section>

          {/* Internal Links */}
          <section className="py-12 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">Explore More ADU Designs</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/modern-adu-design-san-carlos" className="text-purple-600 hover:text-purple-700 text-center p-4 rounded-lg hover:bg-purple-50 transition-colors">
                  Modern ADU Design San Carlos
                </Link>
                <Link to="/craftsman-adu-design-san-carlos" className="text-purple-600 hover:text-purple-700 text-center p-4 rounded-lg hover:bg-purple-50 transition-colors">
                  Craftsman ADU Design San Carlos
                </Link>
                <Link to="/transitional-adu-design-san-carlos" className="text-purple-600 hover:text-purple-700 text-center p-4 rounded-lg hover:bg-purple-50 transition-colors">
                  Transitional ADU Design San Carlos
                </Link>
                <Link to="/contemporary-adu-design-san-carlos" className="text-purple-600 hover:text-purple-700 text-center p-4 rounded-lg hover:bg-purple-50 transition-colors">
                  Contemporary ADU Design San Carlos
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingADUWizard />
        <FloatingReserveButton />
      </div>
    </>
  );
};

export default VictorianADUDesignSanCarlos;